import {
  eachDayOfInterval,
  eachMonthOfInterval,
  endOfMonth,
  endOfWeek,
  endOfYear,
  format,
  isMatch,
  parse,
  startOfMonth,
  startOfWeek,
  startOfYear,
  isValid
} from 'date-fns'
import { useCallback } from 'react'

export const getDaysByWeek = (referenceDay, locale) => {
  if (!referenceDay || !isValid(referenceDay)) {
    const fallbackDate = new Date()
    referenceDay = fallbackDate
  }

  const firstDayOfMonth = startOfMonth(referenceDay, { locale })
  const lastDayOfMonth = endOfMonth(referenceDay, { locale })
  const firstDayOfFirstWeek = startOfWeek(firstDayOfMonth, { locale })
  const lastDayOfLastWeek = endOfWeek(lastDayOfMonth, { locale })

  if (firstDayOfFirstWeek >= lastDayOfLastWeek) {
    return []
  }

  const daysToSort = eachDayOfInterval({
    start: firstDayOfFirstWeek,
    end: lastDayOfLastWeek
  })
  
  const daysByWeek = []
  while (daysToSort.length) {
    const week = []
    let toIndex = 0
    daysToSort.some(day => {
      week.push(day)
      toIndex++
      return day.getDay() === 0
    })
    daysByWeek.push(week)
    daysToSort.splice(0, toIndex)
  }
  return daysByWeek
}

export const getWeekdays = locale => {
  const now = new Date()
  if (!isValid(now)) {
    return []
  }
  
  const start = startOfWeek(now, { locale })
  const end = endOfWeek(now, { locale })
  
  if (start >= end) {
    return []
  }
  
  return eachDayOfInterval({
    start,
    end
  }).map(day => format(day, 'cccccc', { locale }))
}

export const getMonthsNames = (locale, t) => {
  const now = new Date()
  if (!isValid(now)) {
    return []
  }
  
  const start = startOfYear(now)
  const end = endOfYear(now)
  
  if (start >= end) {
    return []
  }
  
  const months = eachMonthOfInterval({
    start,
    end
  }).map(month => format(month, 'MMMM', { locale }))

  return months.map((month, index) => t(`months.${index}`, month))
}

export const dateToPFormat = (dateToFormat, locale) => {
  if (!dateToFormat || !isValid(dateToFormat)) {
    return ''
  }
  return format(dateToFormat, 'P', { locale })
}

export const pFormatToDate = (formattedDate, locale) => {
  if (!formattedDate || formattedDate.length === 0 || formattedDate.trim() === '') {
    return null
  }

  const parsed = parse(formattedDate, 'P', new Date(), { locale })
  return isValid(parsed) ? parsed : null
}

const isValidDateFormat = (inputValue, locale) => {
  const expectedLength = 10
  
  if (inputValue.length !== expectedLength) {
    return false
  }
  
  if (!isMatch(inputValue, 'P', { locale })) {
    return false
  }
  
  const parsed = parse(inputValue, 'P', new Date(), { locale })
  if (!isValid(parsed)) {
    return false
  }
  
  const reformatted = format(parsed, 'P', { locale })
  return reformatted === inputValue
}

export const useValidation = ({ locale }) => {
  const validateStartDate = useCallback(
    inputValue => {
      if (!inputValue || inputValue.trim().length === 0) {
        return { isValid: false, error: 'datepicker.errors.empty_date' }
      }
      if (!isValidDateFormat(inputValue, locale)) {
        return { isValid: false, error: 'datepicker.errors.invalid_format' }
      }
      return { isValid: true, error: null }
    },
    [locale]
  )

  const validateEndDate = useCallback(
    inputValue => {
      if (!inputValue || inputValue.trim().length === 0) {
        return { isValid: false, error: 'datepicker.errors.empty_date' }
      }
      if (!isValidDateFormat(inputValue, locale)) {
        return { isValid: false, error: 'datepicker.errors.invalid_format' }
      }
      return { isValid: true, error: null }
    },
    [locale]
  )

  return { validateEndDate, validateStartDate }
}
